import React from 'react'
import { useTranslation } from 'react-i18next'
import ZionForm, { useZionForm } from '@fs/zion-form'
import { ContentAdd, ContentEdit, ContentCheck } from '@fs/zion-icon'
import { InlineButton, DataBlock, Row, Skeleton, useOverlay, QuickGlanceActions, QuickGlanceOverlay } from '@fs/zion-ui'
import { CancelButton, SubmitButton } from '@fs/identity.js'
import { canDisplayAsFormError } from '../../errors'
import { useErrorReporter } from '../../hooks'

function Empty() {
  return ''
}

const dataSeparator = <div style={{ height: 4 }} />

const editButtonMarginStyle = {
  marginTop: '12px',
  marginInlineEnd: '0px',
  marginBottom: '12px',
  marginInlineStart: '4px',
}

const bottomElementSeparator = <div style={{ height: 8 }} />

const bottomElementLoaderSeparator = <div style={{ height: 13 }} />

export default function EditableDataBlock({
  heading,
  id,
  data,
  bottomElement,
  loading = false,
  disabled = false,
  customOverlay,
  noAutoClose = false,
  verify = false,
  handleVerify,
  noEdit = false,
  onClick,
  ...props
}) {
  const newOverlay = useOverlay()
  const overlay = customOverlay ?? newOverlay
  const { t } = useTranslation()
  if (loading) {
    return (
      <EditableDataBlock
        heading={<Skeleton.TypeSkeleton maxWidth={180} />}
        data={<Skeleton.TypeSkeleton maxWidth={250} />}
        bottomElement={
          <>
            <Skeleton.TypeSkeleton maxWidth={250} />
            {bottomElementLoaderSeparator}
          </>
        }
        disabled
      />
    )
  }

  const editButton = (
    <InlineButton id={`${id}-edit-button`} emphasis="low" onClick={onClick || overlay.handleClick} disabled={disabled}>
      {data ? <ContentEdit size="xs" /> : <ContentAdd size="xs" />}
      <span style={editButtonMarginStyle}>
        {data ? t('common-ui:edit.action', 'Edit') : t('common-ui:add.action', 'Add')}
      </span>
    </InlineButton>
  )

  const verifyButton = (
    <InlineButton id={`${id}-verify-button`} emphasis="low" onClick={handleVerify}>
      <ContentCheck size="xs" />
      <span style={editButtonMarginStyle}>{t('verify.action', 'Verify')}</span>
    </InlineButton>
  )

  const dataWithButton = (
    <>
      {dataSeparator}
      <Row alignY="middle" gutters="xxs" guttersY="xs" wrap>
        <div id={`${id}-data`}>{data || <Empty />}</div>
        {!noEdit && editButton}
        {!noEdit && verify && verifyButton}
      </Row>
    </>
  )

  return (
    <div id={`${id}-data-block`}>
      <DataBlock heading={heading} data={dataWithButton} />
      {bottomElement && data ? (
        <>
          {bottomElementSeparator}
          {bottomElement}
        </>
      ) : null}
      {!disabled && overlay.isOpen && <EditableDataBlockOverlay {...{ heading, overlay, noAutoClose, ...props }} />}
    </div>
  )
}

function EditableDataBlockOverlay({
  heading,
  overlay,
  as = ZionForm,
  mode = 'onSubmit',
  defaultValues,
  resolver,
  onSubmit,
  footer,
  children,
  submitButtonProps,
  noAutoClose = false,
  ...props
}) {
  const methods = useZionForm({
    mode,
    defaultValues,
    resolver,
  })
  const {
    formState: { isSubmitting },
  } = methods
  const reportError = useErrorReporter()
  const handleSubmit = async (values) => {
    try {
      await onSubmit(values, methods)
      !noAutoClose && overlay.close()
    } catch (error) {
      const fieldNames = Object.keys(defaultValues || {})
      if (canDisplayAsFormError(error, fieldNames)) {
        methods.setError(error.fieldName, { type: 'manual', message: error.render() }, { shouldFocus: true })
      } else {
        reportError(error)
      }
    }
  }
  return (
    <QuickGlanceOverlay
      {...overlay}
      placement="bottom"
      title={heading}
      as={as}
      methods={methods}
      onSubmit={handleSubmit}
      {...props}
    >
      {children}
      <QuickGlanceActions>
        <CancelButton
          onClick={() => {
            overlay.close()
          }}
        />
        <SubmitButton isSubmitting={isSubmitting} {...submitButtonProps} />
      </QuickGlanceActions>
    </QuickGlanceOverlay>
  )
}
